import React from 'react';

/* 
  Layout system:
  - Mobile (< 640px):  full-screen app, bottom nav
  - Tablet (640-1024px): centered card with bottom nav
  - Desktop (> 1024px): sidebar nav + main content area
*/

export const useBreakpoint = () => {
  const [bp, setBp] = React.useState(() => {
    const w = window.innerWidth;
    if (w >= 1024) return 'desktop';
    if (w >= 640) return 'tablet';
    return 'mobile';
  });
  React.useEffect(() => {
    const handler = () => {
      const w = window.innerWidth;
      setBp(w >= 1024 ? 'desktop' : w >= 640 ? 'tablet' : 'mobile');
    };
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return bp;
};

export const AppShell = ({ t, children, sidebar, bottomNav, topBar }) => {
  const bp = useBreakpoint();

  if (bp === 'desktop') {
    return (
      <div style={{ display: 'flex', height: '100vh', background: t.isDark ? '#020305' : '#E8F0E8', fontFamily: 'Nunito, sans-serif' }}>
        {/* Sidebar */}
        {sidebar && (
          <div style={{ width: 260, flexShrink: 0, background: t.surface, borderRight: `1px solid ${t.border}`, display: 'flex', flexDirection: 'column', height: '100vh' }}>
            {sidebar}
          </div>
        )}
        {/* Main content */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {topBar}
          <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
            <div style={{ maxWidth: 900, margin: '0 auto' }}>
              {children}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (bp === 'tablet') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: t.isDark ? '#020305' : '#E8F0E8', fontFamily: 'Nunito, sans-serif', padding: 20 }}>
        <div style={{ width: 540, maxWidth: '100%', height: '90vh', background: t.bg, borderRadius: 24, overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: t.isDark ? '0 0 60px rgba(129,199,132,.1), 0 20px 60px rgba(0,0,0,.6)' : '0 10px 50px rgba(0,0,0,.15)' }}>
          {topBar}
          <div style={{ flex: 1, overflowY: 'auto' }}>{children}</div>
          {bottomNav}
        </div>
      </div>
    );
  }

  // Mobile
  return (
    <div style={{ width: '100vw', height: '100vh', background: t.bg, display: 'flex', flexDirection: 'column', overflow: 'hidden', fontFamily: 'Nunito, sans-serif' }}>
      {topBar}
      <div style={{ flex: 1, overflowY: 'auto' }}>{children}</div>
      {bottomNav}
    </div>
  );
};

export const Sidebar = ({ t, tabs, active, onTab, isAdmin, onSwitchMode }) => (
  <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
    {/* Logo */}
    <div style={{ background: t.isDark ? '#040f08' : '#1B5E20', padding: '20px 20px 16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'rgba(255,255,255,.15)', border: '2px solid #C8A400', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 900, color: '#C8A400' }}>SB</div>
        <div>
          <div style={{ color: '#fff', fontSize: 15, fontWeight: 900 }}>SB Group</div>
          <div style={{ color: '#A5D6A7', fontSize: 11, fontWeight: 600 }}>Something Bigger</div>
        </div>
      </div>
      <div style={{ background: 'rgba(255,255,255,.1)', borderRadius: 8, padding: '8px 12px' }}>
        <div style={{ color: '#A5D6A7', fontSize: 11, fontWeight: 600 }}>{isAdmin ? 'Admin Panel' : 'Member'}</div>
        <div style={{ color: '#fff', fontSize: 13, fontWeight: 800 }}>{isAdmin ? 'Md. Sumon Sarker' : 'Rasel Ahmed'}</div>
      </div>
    </div>
    {/* Nav items */}
    <div style={{ flex: 1, overflowY: 'auto', padding: '12px 12px' }}>
      {tabs.map(tab => (
        <div key={tab.key} onClick={() => onTab(tab.key)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderRadius: 10, marginBottom: 4, cursor: 'pointer', background: active === tab.key ? t.primaryLight : 'transparent', border: active === tab.key ? `0.5px solid ${t.borderLight}` : '0.5px solid transparent' }}>
          <span style={{ fontSize: 18 }}>{tab.icon}</span>
          <span style={{ fontSize: 13, fontWeight: active === tab.key ? 800 : 600, color: active === tab.key ? t.primary : t.textSec }}>{tab.label}</span>
          {tab.badge && <span style={{ marginLeft: 'auto', background: '#C62828', color: '#fff', borderRadius: 10, padding: '1px 7px', fontSize: 11, fontWeight: 800 }}>{tab.badge}</span>}
        </div>
      ))}
    </div>
    {/* Bottom switch */}
    {onSwitchMode && (
      <div style={{ padding: 12, borderTop: `0.5px solid ${t.border}` }}>
        <div onClick={onSwitchMode} style={{ background: t.primaryLight, border: `0.5px solid ${t.borderLight}`, borderRadius: 10, padding: '10px 14px', cursor: 'pointer', textAlign: 'center', fontSize: 13, fontWeight: 700, color: t.primary }}>
          Switch to {isAdmin ? 'Member' : 'Admin'} view
        </div>
      </div>
    )}
  </div>
);

export const BottomNav = ({ t, tabs, active, onTab }) => (
  <div style={{ background: t.navBg, borderTop: `0.5px solid ${t.border}`, display: 'flex', flexShrink: 0, paddingBottom: 'env(safe-area-inset-bottom)' }}>
    {tabs.map(tab => (
      <div key={tab.key} onClick={() => onTab(tab.key)} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '8px 4px 5px', cursor: 'pointer', position: 'relative' }}>
        {tab.badge && <div style={{ position: 'absolute', top: 4, right: '50%', transform: 'translateX(12px)', background: '#C62828', color: '#fff', borderRadius: 8, width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 900 }}>{tab.badge}</div>}
        <span style={{ fontSize: 22 }}>{tab.icon}</span>
        <span style={{ fontSize: 10, color: active === tab.key ? t.primary : t.textHint, fontWeight: active === tab.key ? 800 : 600, marginTop: 1 }}>{tab.label}</span>
        {active === tab.key && <div style={{ width: 20, height: 2, background: t.primary, borderRadius: 1, marginTop: 2 }} />}
      </div>
    ))}
  </div>
);

export const DesktopTopBar = ({ t, title, right }) => (
  <div style={{ background: t.isDark ? '#040f08' : '#1B5E20', padding: '14px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
    <span style={{ color: '#fff', fontSize: 18, fontWeight: 800 }}>{title}</span>
    {right}
  </div>
);
